import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

// Mobile polish for an open modal/sheet: locks background scroll while
// open, and treats the OS back button / Android back gesture as a "close
// this modal" action instead of navigating away. Apply the companion
// `overscroll-contain` Tailwind class on the sheet element itself so
// swipes inside it can't trigger iOS pull-to-refresh.
//
// Pass `containerRef` (the dialog element, given `tabIndex={-1}`) to also
// get desktop/keyboard a11y: focus moves into the dialog on open, Tab is
// trapped inside it, and focus is restored to the trigger on close.
//
// Call from the component body unconditionally (before any early return)
// so the hook order is stable.
export function useModalChrome(
  open: boolean,
  onClose: () => void,
  containerRef?: RefObject<HTMLElement | null>,
): void {
  // Keep the latest onClose in a ref so the back-gesture effect below depends
  // only on `open`. Otherwise a fresh `onClose` arrow on every parent render
  // re-runs that effect while the modal is open — its cleanup calls
  // history.back(), whose popstate the just-re-added listener catches and
  // treats as a close, slamming the modal shut the instant it opens.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Lock background scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Intercept the back gesture: push a history entry on open, pop it (or
  // call onClose if the user popped it themselves) on close.
  useEffect(() => {
    if (!open) return;
    const onPop = () => onCloseRef.current();
    history.pushState({ modal: true }, "");
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      // Modal closed via the close button (not back): pop our entry so
      // the history stack stays clean. If the close already came from a
      // popstate, our state is already gone — the guard skips the back().
      const s = history.state as { modal?: boolean } | null;
      if (s && s.modal) history.back();
    };
  }, [open]);

  // Focus management (only when a container ref is supplied). Moves focus
  // into the dialog on open, traps Tab within it, restores focus on close.
  useEffect(() => {
    if (!open || !containerRef) return;
    const root = containerRef.current;
    if (!root) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Don't fight a component's own autoFocus — only seed focus if nothing
    // inside the dialog already has it. Seed now and again on the next frame:
    // a click that opened the dialog can leave focus settling on the trigger
    // (or body) after this passive effect first runs.
    const seed = () => {
      if (!root.contains(document.activeElement)) {
        const first = root.querySelector<HTMLElement>(FOCUSABLE);
        (first ?? root).focus();
      }
    };
    seed();
    const raf = requestAnimationFrame(seed);

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusable = Array.from(
        root.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKey);
      previouslyFocused?.focus?.();
    };
  }, [open, containerRef]);
}
